import { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL - handles both old and new formats
const getImageUrl = (url) => {
  if (!url) return '';
  // Already a full URL
  if (url.startsWith('http')) {
    // Fix old format: if URL contains /uploads/ without /api/, add /api
    if (url.includes('/uploads/') && !url.includes('/api/uploads/')) {
      return url.replace('/uploads/', '/api/uploads/');
    }
    return url;
  }
  // Relative path - add BACKEND_URL
  // If path doesn't start with /api, add it
  if (url.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${url}`;
  }
  return `${BACKEND_URL}${url}`;
};

const RecipesPage = () => {
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  useEffect(() => {
    fetchRecipes();
  }, []);

  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes`);
      setRecipes(response.data);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1915]">
      <Navigation />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(./images/recipes.webp)',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="relative z-10 text-center px-6">
          <h1 className="playfair text-5xl lg:text-7xl font-bold text-white mb-6" data-testid="recipes-page-title">
            The Art of Legato
          </h1>
          <div className="w-32 h-1 bg-[#C9A961] mx-auto mb-6"></div>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Elevate your culinary creations with our premium olive oil recipes
          </p>
        </div>
      </section>

      {/* Recipes Grid */}
      <section className="py-20 px-6 bg-[#1A1915] stone-texture">
        <div className="max-w-7xl mx-auto">
          {recipes.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recipes.map((recipe, index) => (
                <div
                  key={recipe.id}
                  data-testid={`recipe-item-${index}`}
                  onClick={() => setSelectedRecipe(recipe)}
                  className="group cursor-pointer relative overflow-hidden rounded-lg shadow-xl hover:shadow-2xl hover:shadow-[#C9A961]/20 transition-all duration-300"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={getImageUrl(recipe.image_url)}
                      alt={recipe.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent flex items-end">
                    <div className="p-6 w-full">
                      <span className="text-xs text-[#C9A961] uppercase tracking-wider">
                        {recipe.category}
                      </span>
                      <h3 className="playfair text-xl font-semibold text-white mt-2 mb-2">
                        {recipe.title}
                      </h3>
                      <p className="text-gray-300 text-sm line-clamp-2">
                        {recipe.description}
                      </p>
                      <button className="mt-4 text-[#C9A961] text-sm font-semibold hover:underline">
                        View Recipe →
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="text-6xl mb-6">🍳</div>
              <h3 className="playfair text-2xl text-gray-400 mb-4">
                No recipes available yet
              </h3>
              <p className="text-gray-500">
                Check back soon for delicious recipes featuring our olive oil
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Recipe Modal */}
      {selectedRecipe && (
        <div
          className="fixed inset-0 bg-[#1A1915]/90 z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedRecipe(null)}
        >
          <div
            className="bg-[#2A2720] rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-64 md:h-96">
              <img
                src={getImageUrl(selectedRecipe.image_url)}
                alt={selectedRecipe.title}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => setSelectedRecipe(null)}
                data-testid="close-recipe-modal"
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#1A1915]/70 text-white hover:bg-[#C9A961] transition-colors flex items-center justify-center"
              >
                ✕
              </button>
            </div>
            <div className="p-8">
              <span className="text-xs text-[#C9A961] uppercase tracking-wider">
                {selectedRecipe.category}
              </span>
              <h2 className="playfair text-3xl font-bold text-white mt-2 mb-4">
                {selectedRecipe.title}
              </h2>
              <p className="text-gray-300 text-lg mb-8">
                {selectedRecipe.description}
              </p>

              <div className="mb-8">
                <h3 className="text-[#C9A961] font-semibold text-xl mb-4">Ingredients</h3>
                <ul className="space-y-2">
                  {selectedRecipe.ingredients.map((ingredient, index) => (
                    <li key={index} className="text-gray-300 flex items-start">
                      <span className="text-[#C9A961] mr-2">•</span>
                      {ingredient}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-[#C9A961] font-semibold text-xl mb-4">Instructions</h3>
                <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                  {selectedRecipe.instructions}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default RecipesPage;